import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthService } from './AuthService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const authService = new AuthService();

  // Check for existing token on app start (PWA persistence)
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        console.log('🔍 AuthContext: Checking auth status...');
        
        // Check both token keys for backward compatibility
        const storedToken = localStorage.getItem('auth_token') || localStorage.getItem('token');
        const preloadedUser = localStorage.getItem('user_data');
        
        console.log('🔍 AuthContext: Found tokens:', {
          hasAuthToken: !!localStorage.getItem('auth_token'),
          hasToken: !!localStorage.getItem('token'),
          hasPreloadedUser: !!preloadedUser
        });
        
        if (storedToken) {
          // If we have pre-loaded user data (from admin login), use it immediately
          if (preloadedUser) {
            try {
              console.log('📦 AuthContext: Using pre-loaded user data');
              const userData = JSON.parse(preloadedUser);
              localStorage.setItem('auth_token', storedToken);
              localStorage.setItem('token', storedToken);
              setToken(storedToken);
              setUser(userData);
              localStorage.removeItem('user_data'); // Clean up after use
              console.log('✅ AuthContext: User loaded from pre-loaded data:', userData.email);
              setLoading(false);
              return;
            } catch (e) {
              console.error('❌ AuthContext: Error parsing pre-loaded user data:', e);
            }
          }
          
          // Otherwise, fetch user info from API
          console.log('📡 AuthContext: Fetching user info from API...');
          const userInfo = await authService.getCurrentUser(storedToken);
          if (userInfo) {
            // Sync both token keys
            localStorage.setItem('auth_token', storedToken);
            localStorage.setItem('token', storedToken);
            setToken(storedToken);
            setUser(userInfo);
            console.log('✅ AuthContext: User loaded from API:', userInfo.email);
          } else {
            console.log('❌ AuthContext: Token invalid, clearing storage');
            // Token is invalid, remove both
            localStorage.removeItem('auth_token');
            localStorage.removeItem('token');
            localStorage.removeItem('user_data');
          }
        } else {
          console.log('ℹ️ AuthContext: No token found');
        }
      } catch (error) {
        console.error('❌ AuthContext: Auth check failed:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
        localStorage.removeItem('user_data');
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      
      // Store token (both keys for backward compatibility)
      localStorage.setItem('auth_token', response.access_token);
      localStorage.setItem('token', response.access_token); // For PWA persistence
      setToken(response.access_token);
      setUser(response.user);
      
      // DON'T redirect here - let the calling component handle navigation
      // This prevents full page reload and maintains React context
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  const register = async (name, email, password, additionalData = {}) => {
    try {
      const response = await authService.register({
        name,
        email,
        password,
        ...additionalData
      });
      
      // Store token (both keys for backward compatibility)
      localStorage.setItem('auth_token', response.access_token);
      localStorage.setItem('token', response.access_token); // For PWA persistence
      setToken(response.access_token);
      setUser(response.user);
      
      // DON'T redirect here - let the calling component handle navigation
      // This prevents full page reload and maintains React context
      
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token'); // Remove both keys
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (updateData) => {
    try {
      const updatedUser = await authService.updateProfile(token, updateData);
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      throw error;
    }
  };

  const uploadProfilePicture = async (file) => {
    try {
      const response = await authService.uploadProfilePicture(token, file);
      setUser(response.user);
      return response;
    } catch (error) {
      throw error;
    }
  };

  const isAuthenticated = !!token && !!user;

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    updateProfile,
    uploadProfilePicture
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};